// app/javascript/controllers/discount_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        // Discount
        "percent", "fixed", "preview", "amountHidden",
        // Add-on (NEW)
        "addonLabel", "addonAmount", "addonPreview", "addonHidden"
    ]

    shouldApply() {
        return this.percentTarget?.value > 0 || this.fixedTarget?.value > 0 || this.addonAmountTarget?.value > 0
    }

    apply() {
        const form = this.element.closest("form")
        const subtotalHidden = form?.querySelector('[data-quotation-target="subtotalHidden"]')
        const totalHidden    = form?.querySelector('[data-quotation-target="totalHidden"]')
        const totalDisplay   = form?.querySelector('[data-quotation-target="totalDisplay"]')

        // 1) Read current subtotal
        let subtotal = 0.0
        if (subtotalHidden && subtotalHidden.value !== "") subtotal = parseFloat(subtotalHidden.value)
        if (Number.isNaN(subtotal)) {
            // fallback from visible total (when subtotal not filled yet)
            if (totalDisplay) {
                const raw = (totalDisplay.textContent || "").replace(/[₱,\s]/g, "")
                subtotal = parseFloat(raw)
            }
        }
        if (Number.isNaN(subtotal) || subtotal < 0) {
            this._renderDiscountPreview(0, 0, 0)
            this._renderAddonPreview(0, "Add-on")
            return
        }

        // 2) Discount
        const pct   = Math.max(0, parseFloat(this.percentTarget?.value || "0"))
        const fixed = Math.max(0, parseFloat(this.fixedTarget?.value || "0"))
        const percentCut   = pct > 0 ? (subtotal * (pct / 100)) : 0
        const afterPercent = Math.max(0, subtotal - percentCut)
        const fixedApplied = Math.max(0, Math.min(fixed, afterPercent))
        const totalDiscount = percentCut + fixedApplied
        const afterDiscount = Math.max(0, subtotal - totalDiscount)

        // 3) Add-on
        const addonVal = Math.max(0, parseFloat(this.addonAmountTarget?.value || "0"))
        const addonLbl = (this.hasAddonLabelTarget && (this.addonLabelTarget.value || "").trim() !== "")
            ? this.addonLabelTarget.value.trim()
            : "Add-on"

        // 4) Final total
        const newTotal = afterDiscount + addonVal

        // 5) Persist discount & total (and optional add-on hidden)
        if (this.hasAmountHiddenTarget) this.amountHiddenTarget.value = totalDiscount.toFixed(2)
        if (this.hasAddonHiddenTarget)  this.addonHiddenTarget.value  = addonVal.toFixed(2)
        if (totalHidden)                totalHidden.value            = newTotal.toFixed(2)

        // 6) Update UI
        if (totalDisplay) totalDisplay.textContent = this._fmtNumber(newTotal)
        this._renderDiscountPreview(totalDiscount, percentCut, fixedApplied)
        this._renderAddonPreview(addonVal, addonLbl)
    }

    _renderDiscountPreview(totalDiscount, percentCut, fixedApplied) {
        if (!this.hasPreviewTarget) return
        let text
        if (percentCut > 0 && fixedApplied > 0) {
            text = `− ${this._fmtCurrency(totalDiscount)} (${this._fmtNumber(percentCut)} + ${this._fmtNumber(fixedApplied)} fixed)`
        } else if (percentCut > 0) {
            text = `− ${this._fmtCurrency(totalDiscount)} (percent)`
        } else if (fixedApplied > 0) {
            text = `− ${this._fmtCurrency(totalDiscount)} (fixed)`
        } else {
            text = `− ${this._fmtCurrency(0)}`
        }
        this.previewTarget.textContent = text
    }

    _renderAddonPreview(amount, label) {
        if (!this.hasAddonPreviewTarget) return
        this.addonPreviewTarget.textContent = `+ ${this._fmtCurrency(amount)} ${label} applied`
    }

    _fmtCurrency(n) {
        try {
            return new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP", minimumFractionDigits: 2 }).format(n)
        } catch { return `₱ ${Number(n).toFixed(2)}` }
    }
    _fmtNumber(n) {
        try {
            return new Intl.NumberFormat("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
        } catch { return Number(n).toFixed(2) }
    }
}
